// src/pages/Admin.jsx
// 관리자 전용 자동화 대시보드 (Phase 1):
//   1) 한미일 정치 뉴스 트렌딩 수집
//   2) 국가별 기사 1건 선택
//   3) 6개 쇼츠 자동 생성 (국가 × 진보/보수) — 대본·유튜브 메타 자동 작성
//   4) 생성 큐에서 검수 (유튜브 업로드는 Phase 2)
// 내부 운영 도구이므로 UI 텍스트는 한국어 하드코딩 (단일 관리자 전용).

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';

import { db } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePlanGuard } from '../hooks/usePlanGuard.js';
import { api } from '../api/citizencast.js';
import { renderShort } from '../api/pipeline.js';
import { EMOTION_LABELS } from '../utils/anchorVoices.js';
import SEO from '../components/SEO.jsx';

// 배포된 채널 로고(영상 오버레이용 원형 뱃지, 투명배경)의 공개 URL — Creatomate 가 직접 fetch
const SITE_URL = 'https://citizen-cast.com';
const logoUrlFor = (channel) => `${SITE_URL}/channel-art/${channel}-logo.png`;

const COUNTRIES = ['KR', 'US', 'JP'];
const COUNTRY_LABEL = { KR: '🇰🇷 한국', US: '🇺🇸 미국', JP: '🇯🇵 일본' };
const STANCES = ['liberal', 'conservative'];
const STANCE_LABEL = { liberal: '진보', conservative: '보수' };

function channelKey(country, stance) {
  return `${country.toLowerCase()}_${stance}`;
}

function STEP_LABEL(step) {
  switch (step) {
    case 'script': return '대본 생성 중…';
    case 'meta': return '유튜브 메타 작성 중…';
    case 'rendering': return '렌더 시작 중…';
    case 'queued': return '✅ 큐 등록됨';
    default:
      if (step && step.startsWith('error:')) return `❌ ${step.slice(6)}`;
      return step || '';
  }
}

function statusBadge(status) {
  switch (status) {
    case 'done': return { label: '완료', className: 'bg-green-100 text-green-700' };
    case 'rendering': return { label: '렌더 중', className: 'bg-blue-100 text-blue-700' };
    case 'pending': return { label: '대기', className: 'bg-gray-100 text-gray-700' };
    case 'failed': return { label: '실패', className: 'bg-red-100 text-red-700' };
    default: return { label: status || '?', className: 'bg-gray-100 text-gray-700' };
  }
}

function fmtDate(ts) {
  const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function copy(text, msg) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(msg || '복사됨'),
    () => toast.error('복사 실패'),
  );
}

export default function Admin() {
  const { user, profile } = useAuth();
  const { isAdmin } = usePlanGuard();

  const [newsByCountry, setNewsByCountry] = useState({ KR: [], US: [], JP: [] });
  const [loadingNews, setLoadingNews] = useState(false);
  const [selected, setSelected] = useState({ KR: null, US: null, JP: null });
  const [emotionTone, setEmotionTone] = useState('neutral');
  const [generating, setGenerating] = useState(false);
  const [genLog, setGenLog] = useState({}); // channelKey -> step string

  const [queue, setQueue] = useState([]);
  const [ytStatus, setYtStatus] = useState({}); // channelKey -> { connected, channelTitle }
  const [uploading, setUploading] = useState({}); // videoId -> bool
  const [deleting, setDeleting] = useState({}); // videoId -> bool

  const setLog = useCallback((channel, step) => {
    setGenLog((prev) => ({ ...prev, [channel]: step }));
  }, []);

  const loadYtStatus = useCallback(async () => {
    if (!profile?.email) return;
    try {
      const res = await api.youtubeStatus(profile.email);
      setYtStatus(res.channels || {});
    } catch (e) {
      console.error('[admin] yt status err:', e?.message);
    }
  }, [profile?.email]);

  // 생성 큐 구독 — Dashboard 와 같은 (uid + createdAt) 인덱스 재사용, isAuto 만 클라이언트 필터
  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'videos'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(40),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setQueue(all.filter((v) => v.isAuto));
      },
      (err) => console.error('[admin] queue query err:', err),
    );
    return unsub;
  }, [user]);

  // YouTube 연결 현황 로드 + OAuth 콜백 결과 처리
  useEffect(() => {
    if (!isAdmin) return;
    loadYtStatus();
    const p = new URLSearchParams(window.location.search);
    const yt = p.get('yt');
    if (yt === 'connected') toast.success(`✅ 채널 연결됨: ${p.get('channel') || ''}`);
    else if (yt === 'denied') toast.error('연결이 취소되었습니다.');
    else if (yt === 'error') toast.error(`연결 실패 (${p.get('reason') || ''})`);
    if (yt) window.history.replaceState({}, '', '/admin');
  }, [isAdmin, loadYtStatus]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <SEO title="Admin" description="관리자" path="/admin" noIndex />
        <p className="text-gray-500">관리자 전용 페이지입니다.</p>
        <Link to="/dashboard" className="btn-primary inline-block mt-4">대시보드로</Link>
      </div>
    );
  }

  async function handleCollect() {
    setLoadingNews(true);
    try {
      const res = await api.collectNews(profile.email, { limit: 12 });
      setNewsByCountry(res.news || { KR: [], US: [], JP: [] });
      setSelected({ KR: null, US: null, JP: null });
    } catch (e) {
      toast.error(`뉴스 수집 실패: ${e.message || ''}`);
    } finally {
      setLoadingNews(false);
    }
  }

  async function handleConnect(channel) {
    try {
      const res = await api.youtubeOauthStart(profile.email, channel);
      window.location.href = res.url; // 구글 동의 화면으로 이동
    } catch (e) {
      toast.error(`연결 시작 실패: ${e.message || ''}`);
    }
  }

  async function handleUpload(v) {
    setUploading((prev) => ({ ...prev, [v.id]: true }));
    try {
      const res = await api.youtubeUpload(profile.email, v.id, 'public');
      toast.success(`✅ 업로드 완료: ${res.url}`);
    } catch (e) {
      toast.error(`업로드 실패: ${e.message || ''}`);
    } finally {
      setUploading((prev) => ({ ...prev, [v.id]: false }));
    }
  }

  async function handleDeleteVideo(v) {
    if (!confirm(`이 영상을 삭제할까요?\n\n${v.youtubeMeta?.title || v.newsTitle || v.id}\n\n(Firestore/스토리지만 정리 — 이미 유튜브에 게시된 영상은 삭제되지 않습니다)`)) return;
    setDeleting((prev) => ({ ...prev, [v.id]: true }));
    try {
      await api.adminDeleteVideo(profile.email, v.id);
      toast.success('삭제됨');
    } catch (e) {
      toast.error(`삭제 실패: ${e.message || ''}`);
    } finally {
      setDeleting((prev) => ({ ...prev, [v.id]: false }));
    }
  }

  async function handleCleanupStuck() {
    const stuck = queue.filter((v) => ['pending', 'failed'].includes(v.renderStatus));
    if (!stuck.length) {
      toast('정리할 대기/실패 영상이 없습니다.');
      return;
    }
    if (!confirm(`대기/실패 상태 ${stuck.length}건을 삭제할까요?`)) return;
    const tid = toast.loading(`${stuck.length}건 정리 중…`);
    let done = 0;
    for (const v of stuck) {
      try {
        await api.adminDeleteVideo(profile.email, v.id);
        done += 1;
      } catch {
        /* 개별 실패는 건너뜀 */
      }
    }
    toast.success(`${done}/${stuck.length}건 정리 완료`, { id: tid });
  }

  async function handleAutoGenerate(country) {
    const tid = toast.loading(`🤖 ${COUNTRY_LABEL[country]} 생성 중… (진보·보수 2편, 20초 내외)`);
    try {
      const res = await api.autoGenerate(profile.email, country);
      const okCount = (res.results || []).filter((r) => r.ok).length;
      toast.success(`✅ ${COUNTRY_LABEL[country]} ${okCount}/2편 렌더 시작 — 완료되면 자동 게시. 큐를 지켜보세요.`, { id: tid });
    } catch (e) {
      toast.error(`자동 생성 실패: ${e.message || ''}`, { id: tid });
    }
  }

  async function generateChannel(country, article, stance) {
    const channel = channelKey(country, stance);
    try {
      setLog(channel, 'script');
      const scriptRes = await api.generateScript({
        newsTitle: article.title,
        newsContent: article.content,
        stance,
        country,
        userComment: null,
        targetLanguage: null,
        plan: 'tier3',
        emotionTone,
        brandCta: true, // 홍보 영상 — 마지막 씬에 "구글에 브랜드 검색" CTA
      });
      const script = scriptRes.script;

      setLog(channel, 'meta');
      const metaRes = await api.genVideoMeta({
        adminEmail: profile.email,
        country,
        stance,
        scriptTitle: script.title,
        newsTitle: article.title,
        hashtags: script.hashtags || [],
      });
      const meta = metaRes.meta;

      // 대본·메타를 모두 담아 doc 생성 (firestore.rules: 생성 후 추가 필드 갱신 불가)
      const ref = await addDoc(collection(db, 'videos'), {
        uid: user.uid,
        country,
        targetLanguage: null,
        newsUrl: article.link,
        newsTitle: article.title,
        stance,
        userComment: null,
        emotionTone,
        // scenes 도 함께 저장 — 사후 진단/재렌더용. ~2-3KB, 1MB 한도에 무영향.
        script: { title: script.title, hashtags: script.hashtags || [], scenes: script.scenes || [] },
        audioUrl: null,
        renderJobId: null,
        renderStatus: 'pending',
        videoUrl: null,
        creditUsed: 0,
        legalConsentAt: null,
        downloadedAt: null,
        isAuto: true,
        channel: meta.channel,
        youtubeMeta: meta,
        createdAt: serverTimestamp(),
      });

      setLog(channel, 'rendering');
      await renderShort({
        videoId: ref.id,
        uid: user.uid,
        script,
        country,
        targetLanguage: null,
        emotionTone,
        plan: 'tier3',
        logoUrl: logoUrlFor(meta.channel), // 채널별 로고를 모서리에 박음
        forceWatermark: false,             // 큰 워터마크 대신 채널 로고로 브랜딩
        skipCredit: true,
        articleTitle: article.title,
      });

      setLog(channel, 'queued');
    } catch (e) {
      console.error(`[admin] ${channel} 생성 실패`, e);
      setLog(channel, `error:${e.message || '알 수 없는 오류'}`);
    }
  }

  async function handleGenerateAll() {
    const picked = COUNTRIES.filter((c) => selected[c]);
    if (picked.length === 0) {
      toast.error('국가별로 기사를 먼저 선택하세요.');
      return;
    }
    setGenerating(true);
    setGenLog({});
    try {
      for (const country of picked) {
        const sel = selected[country];
        // 기사 본문 추출 (두 논조가 공유)
        let article;
        try {
          const v = await api.validateUrl(sel.link);
          article = { title: v.title || sel.title, content: v.content, link: sel.link };
        } catch (e) {
          STANCES.forEach((s) => setLog(channelKey(country, s), `error:본문 추출 실패 (${e.message || ''})`));
          continue;
        }
        // 진보/보수 순차 생성 (rate limit 회피)
        for (const stance of STANCES) {
          await generateChannel(country, article, stance);
        }
      }
      toast.success('생성 완료 — 아래 큐에서 검수하세요.');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      <SEO title="Admin — 자동 생성" description="관리자 자동 생성" path="/admin" noIndex />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">🛠 자동 생성 콘솔</h1>
          <p className="text-sm text-gray-500">뉴스 수집 → 6채널 쇼츠 생성 → 검수 (업로드는 수동/Phase 2)</p>
        </div>
        <Link to="/dashboard" className="text-sm underline text-gray-500">대시보드 →</Link>
      </div>

      {/* 1) 뉴스 수집 */}
      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg">1. 오늘의 정치 뉴스</h2>
          <button className="btn-primary" onClick={handleCollect} disabled={loadingNews}>
            {loadingNews ? '수집 중…' : '🔄 뉴스 수집'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COUNTRIES.map((c) => (
            <div key={c} className="space-y-2">
              <div className="font-bold text-sm">{COUNTRY_LABEL[c]}</div>
              <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
                {(newsByCountry[c] || []).length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">수집된 뉴스 없음</p>
                ) : (
                  newsByCountry[c].map((n) => {
                    const isSel = selected[c]?.link === n.link;
                    return (
                      <button
                        key={n.link}
                        onClick={() => setSelected((prev) => ({ ...prev, [c]: isSel ? null : n }))}
                        className={`w-full text-left rounded-lg border p-2 text-xs transition ${
                          isSel ? 'border-transparent text-white' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        style={isSel ? { background: 'var(--color-primary)' } : {}}
                      >
                        <div className="font-bold line-clamp-2">{n.title}</div>
                        <div className={`mt-1 ${isSel ? 'opacity-80' : 'text-gray-400'}`}>
                          {n.source} · {n.date}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 2) 생성 */}
      <section className="card space-y-4">
        <h2 className="font-bold text-lg">2. 쇼츠 생성</h2>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-bold">감정톤:</label>
          <select
            className="input !w-auto"
            value={emotionTone}
            onChange={(e) => setEmotionTone(e.target.value)}
            disabled={generating}
          >
            {Object.keys(EMOTION_LABELS).map((tone) => (
              <option key={tone} value={tone}>{EMOTION_LABELS[tone].ko}</option>
            ))}
          </select>

          <span className="text-xs text-gray-500">
            선택됨: {COUNTRIES.filter((c) => selected[c]).map((c) => COUNTRY_LABEL[c]).join(', ') || '없음'}
            {' '}→ 최대 {COUNTRIES.filter((c) => selected[c]).length * 2}개 생성
          </span>

          <button
            className="btn-primary ml-auto"
            onClick={handleGenerateAll}
            disabled={generating || COUNTRIES.every((c) => !selected[c])}
          >
            {generating ? '생성 중…' : '🎬 선택한 국가 × 진보·보수 생성'}
          </button>
        </div>

        {/* 진행 로그 */}
        {Object.keys(genLog).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {COUNTRIES.flatMap((c) =>
              STANCES.map((s) => {
                const ch = channelKey(c, s);
                const step = genLog[ch];
                if (!step) return null;
                return (
                  <div key={ch} className="rounded-lg border p-2 text-xs">
                    <div className="font-bold">{COUNTRY_LABEL[c]} {STANCE_LABEL[s]}</div>
                    <div className="text-gray-600">{STEP_LABEL(step)}</div>
                  </div>
                );
              }),
            )}
          </div>
        )}
      </section>

      {/* 3) 유튜브 채널 연결 */}
      <section className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg">3. 유튜브 채널 연결</h2>
          <button className="text-xs underline text-gray-500" onClick={loadYtStatus}>새로고침</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {COUNTRIES.flatMap((c) =>
            STANCES.map((s) => {
              const ch = channelKey(c, s);
              const st = ytStatus[ch];
              const connected = st?.connected;
              return (
                <div key={ch} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-bold">
                      {COUNTRY_LABEL[c]} {STANCE_LABEL[s]}{' '}
                      <span className="text-xs text-gray-400">({ch})</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {connected ? `✅ ${st.channelTitle || '연결됨'}` : '미연결'}
                    </div>
                  </div>
                  <button
                    className="btn-secondary !py-1 !px-3 text-xs shrink-0"
                    onClick={() => handleConnect(ch)}
                  >
                    {connected ? '재연결' : '연결'}
                  </button>
                </div>
              );
            }),
          )}
        </div>
      </section>

      {/* 4) 자동 포스팅 (완전 자동화) */}
      <section className="card space-y-3">
        <h2 className="font-bold text-lg">4. 자동 포스팅</h2>
        <p className="text-xs text-gray-500">
          국가별로 최신 트렌딩 뉴스 1건을 골라 진보·보수 2편을 생성하고, 렌더 완료 시 해당 채널에 <strong>자동 게시</strong>합니다.
          매일 아침 자동 실행은 외부 스케줄러가 담당하며, 아래 버튼으로 지금 바로 수동 실행할 수도 있습니다.
        </p>
        <div className="flex flex-wrap gap-2">
          {COUNTRIES.map((c) => (
            <button
              key={c}
              className="btn-secondary text-sm"
              onClick={() => handleAutoGenerate(c)}
            >
              🤖 {COUNTRY_LABEL[c]} 지금 실행
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-400">
          ※ 채널이 연결돼 있어야 자동 게시됩니다(위 3번). 일일 업로드 상한(YOUTUBE_DAILY_CAP) 초과분은 게시되지 않습니다.
        </p>
      </section>

      {/* 5) 생성 큐 */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg">5. 생성 큐 ({queue.length})</h2>
          {queue.some((v) => ['pending', 'failed'].includes(v.renderStatus)) && (
            <button className="btn-secondary text-xs !py-1 !px-3" onClick={handleCleanupStuck}>
              🧹 대기/실패 정리
            </button>
          )}
        </div>
        {queue.length === 0 ? (
          <p className="text-sm text-gray-400">아직 자동 생성된 영상이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {queue.map((v) => {
              const badge = statusBadge(v.renderStatus);
              const meta = v.youtubeMeta || {};
              return (
                <div key={v.id} className="card !p-3 flex gap-3">
                  <div className="w-24 shrink-0 aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden relative">
                    {v.videoUrl ? (
                      <video src={v.videoUrl} muted playsInline className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🎬</div>
                    )}
                    <span className={`absolute top-1 right-1 badge ${badge.className}`}>{badge.label}</span>
                  </div>

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold" style={{ color: 'var(--color-primary)' }}>
                        {v.channel || '—'}
                      </span>
                      {fmtDate(v.createdAt) && (
                        <span className="text-[11px] text-gray-400 shrink-0">🕒 {fmtDate(v.createdAt)}</span>
                      )}
                    </div>
                    <div className="text-sm font-bold line-clamp-2">{meta.title || v.newsTitle}</div>
                    <p className="text-xs text-gray-500 line-clamp-3 whitespace-pre-line">
                      {meta.description || ''}
                    </p>
                    {!v.youtubeUpload?.youtubeVideoId && v.youtubeUploadError?.reason && (
                      <p className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1">
                        ⚠ 자동 업로드 실패: {v.youtubeUploadError.reason}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button className="btn-secondary !py-1 !px-2 text-xs" onClick={() => copy(meta.title || '', '제목 복사됨')}>제목 복사</button>
                      <button className="btn-secondary !py-1 !px-2 text-xs" onClick={() => copy(meta.description || '', '설명 복사됨')}>설명 복사</button>
                      {v.videoUrl && (
                        <a href={v.videoUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-1 !px-2 text-xs">⬇ 영상</a>
                      )}
                      <Link to={`/video/${v.id}`} className="btn-secondary !py-1 !px-2 text-xs">보기</Link>
                      {v.youtubeUpload?.youtubeVideoId ? (
                        <a href={v.youtubeUpload.url} target="_blank" rel="noopener noreferrer" className="btn-primary !py-1 !px-2 text-xs">▶ 유튜브 게시됨</a>
                      ) : v.renderStatus === 'done' && ytStatus[v.channel]?.connected ? (
                        <button
                          className="btn-primary !py-1 !px-2 text-xs"
                          disabled={uploading[v.id]}
                          onClick={() => handleUpload(v)}
                        >
                          {uploading[v.id] ? '업로드 중…' : '▶ 업로드'}
                        </button>
                      ) : v.renderStatus === 'done' ? (
                        <span className="text-[11px] text-gray-400 self-center">채널 미연결</span>
                      ) : null}
                      <button
                        className="btn-secondary !py-1 !px-2 text-xs !text-red-600"
                        disabled={deleting[v.id]}
                        onClick={() => handleDeleteVideo(v)}
                        title="Firestore/스토리지에서 삭제"
                      >
                        {deleting[v.id] ? '…' : '🗑'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
